<template>
  <div class="container">
    <div class="row justify-content-center mt-4">
      <div class="col-md-8">
        <div
          class="v3-data-table py-5 px-4"
          :aria-busy="state.isProcessing ? 'true' : 'false'"
        >
          <div
            class="sr-only"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {{ liveStatusMessage }}
          </div>
          <div>
            <StatusBox
              data-test-id="waiting-approval-status-box-label"
              class="mb-2"
              title="waiting approval"
              title-color="#141D24"
              background-color="#308DEB"
              icon="ri-loader-fill"
              icon-color="#141D24"
            />
            <div class="row g-2 pass-times-picker-row">
              <div class="col-12 col-md-6 min-w-0">
                <Label html-for="pass-times-student-passes-time">
                  Student Passes
                </Label>
                <CustomTimePicker
                  data-test-id="waiting-approval-time-picker"
                  input-id="pass-times-student-passes-time"
                  accessible-label="Student Passes"
                  format="HH:mm:ss"
                  picker-time-format="HH:mm:ss"
                  :show-seconds="true"
                  v-model="state.form.white_passes_time"
                ></CustomTimePicker>
              </div>
              <div class="col-12 col-md-6 min-w-0">
                <Label html-for="pass-times-appointment-passes-time">
                  Appointment Passes
                </Label>
                <CustomTimePicker
                  data-test-id="waiting-approval-apt-time-picker"
                  input-id="pass-times-appointment-passes-time"
                  accessible-label="Appointment Passes"
                  format="HH:mm:ss"
                  picker-time-format="HH:mm:ss"
                  :show-seconds="true"
                  v-model="state.form.awaiting_apt_time"
                ></CustomTimePicker>
              </div>
            </div>
          </div>

          <div>
            <StatusBox
              class="mt-4 mb-2"
              title="long running"
              background-color="#FEECBC"
              icon-color="#F3D259"
            />
            <div>
              <Label html-for="pass-times-long-running-time">
                Long Running
              </Label>
              <CustomTimePicker
                data-test-id="long-running-time-picker"
                input-id="pass-times-long-running-time"
                accessible-label="Long Running"
                format="HH:mm:ss"
                picker-time-format="HH:mm:ss"
                :show-seconds="true"
                v-model="state.form.min_time"
              ></CustomTimePicker>
            </div>
          </div>

          <div>
            <StatusBox
              class="mt-4 mb-2"
              title="system ended"
              background-color="#FFE9E2"
              icon-color="#F35531"
            />
            <div>
              <Label html-for="pass-times-system-ended-time">
                System Ended
              </Label>
              <CustomTimePicker
                data-test-id="system-ended-time-picker"
                input-id="pass-times-system-ended-time"
                accessible-label="System Ended"
                format="HH:mm:ss"
                picker-time-format="HH:mm:ss"
                :show-seconds="true"
                v-model="state.form.auto_expire_time"
              ></CustomTimePicker>
            </div>
          </div>

          <div>
            <StatusBox
              class="mt-4 mb-2"
              title="extended"
              background-color="#EEECFD"
              icon-color="#645DF0"
            />
            <div>
              <Label html-for="pass-times-extended-time">Extended</Label>
              <CustomTimePicker
                data-test-id="extended-time-picker"
                input-id="pass-times-extended-time"
                accessible-label="Extended"
                format="HH:mm:ss"
                picker-time-format="HH:mm:ss"
                :show-seconds="true"
                v-model="state.form.nurse_expire_time"
              ></CustomTimePicker>
            </div>
          </div>
          <div v-if="state.feedbackAlert.message" class="mt-4">
            <CAlert
              dismissible
              class="mb-0"
              :color="state.feedbackAlert.color"
              :show="true"
              :role="
                state.feedbackAlert.color === 'danger' ? 'alert' : 'status'
              "
              :aria-live="
                state.feedbackAlert.color === 'danger' ? 'assertive' : 'polite'
              "
              aria-atomic="true"
              data-testid="pass-times-feedback-alert"
              @close="dismissFeedbackAlert"
            >
              <div class="pe-4">
                <div v-if="state.feedbackAlert.title" class="fw-semibold mb-1">
                  {{ state.feedbackAlert.title }}
                </div>
                <div class="mb-0">{{ state.feedbackAlert.message }}</div>
              </div>
            </CAlert>
          </div>
          <div>
            <LoadingButton
              data-test-id="save-pass-times-button"
              :is-loading="state.isProcessing"
              @click="submit()"
              class="px-4 mt-4"
              solid
              rounded
              >Save settings</LoadingButton
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, onMounted, computed } from "vue"
import { useStore } from "vuex"
import StatusBox from "@/v3components/shared/Alerts/StatusBox.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import CustomTimePicker from "@/v3components/shared/Form/CustomTimePicker.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"

export default {
  name: "PassTimesForm",
  components: { StatusBox, Label, CustomTimePicker, LoadingButton },
  setup() {
    const store = useStore()
    const state = reactive({
      isProcessing: false,
      form: {
        white_passes_time: "00:05:00",
        min_time: "00:05:00",
        auto_expire_time: "00:05:00",
        nurse_expire_time: "00:05:00",
        awaiting_apt_time: "00:05:00"
      },
      passTimes: {
        white_passes_time: {
          isEditable: false,
          label: "Awaiting Approval (STU Passes)",
          color: "#fff"
        },
        min_time: {
          isEditable: false,
          label: "Long Running",
          color: "#f4e665"
        },
        auto_expire_time: {
          isEditable: false,
          label: "System Ended",
          color: "#DF3C3B"
        },
        nurse_expire_time: {
          isEditable: false,
          label: "Extended",
          color: "#672c91",
          textColor: "#fff"
        },
        awaiting_apt_time: {
          isEditable: false,
          label: "Awaiting Approval (APT Passes)",
          color: "#fff"
        }
      },
      serverErrors: {},
      feedbackAlert: {
        title: "",
        message: "",
        color: "success"
      }
    })

    onMounted(() => {
      store.dispatch("passes/getPassesTimeSetting").then((response) => {
        const data = response.data.data
        if (data && data.time_settings) {
          setFormData(data.time_settings)
        }
      })
    })

    const setFormData = (data) => {
      state.form = {
        white_passes_time: data.white_passes,
        min_time: data.min_time,
        auto_expire_time: data.auto_expire_time,
        nurse_expire_time: data.nurse_expire_time,
        awaiting_apt_time: data.awaiting_apt
      }
    }

    const submit = () => {
      state.isProcessing = true
      const params = {}
      for (const key in state.form) {
        if (Object.hasOwnProperty.call(state.form, key)) {
          const element = state.form[key]
          if (element) {
            params[key] = element
          }
        }
      }
      store
        .dispatch("passes/setPassesTimeSetting", params)
        .then(() => {
          setFeedbackAlert("Success", "Successfully saved!", "success")
          state.isProcessing = false
          state.serverErrors = {}
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setFeedbackAlert("Error", res.message, "danger")
          state.isProcessing = false
        })
    }

    const setFeedbackAlert = (title, message, color) => {
      if (title || message) {
        state.feedbackAlert = {
          title: title || "",
          message: message || "",
          color: color || "success"
        }
      } else {
        state.feedbackAlert = { title: "", message: "", color: "success" }
      }
    }

    const dismissFeedbackAlert = () => {
      setFeedbackAlert()
    }

    const liveStatusMessage = computed(() => {
      if (state.isProcessing) {
        return "Saving pass times"
      }
      if (state.feedbackAlert?.message) {
        return `${state.feedbackAlert.title || "Status"}: ${state.feedbackAlert.message}`
      }
      return ""
    })

    return {
      state,
      setFormData,
      submit,
      liveStatusMessage,
      dismissFeedbackAlert
    }
  }
}
</script>
